/** Aligned with portal.covomo.de (tabler-admin-shell.js) storage + DOM hooks. */

export type A11yColorScheme = "light" | "dark" | "auto";
export type A11yFontSizeLevel = 0 | 1 | 2;

export interface A11yDisplayPreferences {
  colorScheme: A11yColorScheme;
  fontSizeLevel: A11yFontSizeLevel;
  highContrast: boolean;
}

export const COLOR_STORAGE_KEY = "covomo_tabler_color_mode";
export const HIGH_CONTRAST_STORAGE_KEY = "covomo_tabler_high_contrast";
export const FONT_SIZE_STORAGE_KEY = "covomo_tabler_font_size";
export const AUTO_FROM_STORAGE_KEY = "covomo_tabler_color_mode_auto_from";

export const COLOR_MODES: A11yColorScheme[] = ["light", "dark", "auto"];
export const FONT_SIZE_LEVELS: A11yFontSizeLevel[] = [0, 1, 2];

export const DEFAULT_A11Y_DISPLAY_PREFERENCES: A11yDisplayPreferences = {
  colorScheme: "light",
  fontSizeLevel: 0,
  highContrast: false,
};

export function getSystemPrefersDark(): boolean {
  try {
    return (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  } catch {
    return false;
  }
}

export function readAutoFrom(): "light" | "dark" {
  try {
    const saved = localStorage.getItem(AUTO_FROM_STORAGE_KEY);
    if (saved === "light") return "light";
  } catch {
    /* ignore */
  }
  return "dark";
}

export function writeAutoFrom(from: "light" | "dark"): void {
  try {
    localStorage.setItem(
      AUTO_FROM_STORAGE_KEY,
      from === "light" ? "light" : "dark",
    );
  } catch {
    /* ignore */
  }
}

export function resolveEffectiveColorMode(
  colorScheme: A11yColorScheme,
  systemPrefersDark = getSystemPrefersDark(),
): "light" | "dark" {
  if (colorScheme === "auto") {
    return systemPrefersDark ? "dark" : "light";
  }
  return colorScheme === "dark" ? "dark" : "light";
}

export function readStoredA11yDisplayPreferences(): A11yDisplayPreferences {
  if (typeof localStorage === "undefined") {
    return { ...DEFAULT_A11Y_DISPLAY_PREFERENCES };
  }

  try {
    let colorScheme: A11yColorScheme = "light";
    let highContrast = false;

    const savedColor = localStorage.getItem(COLOR_STORAGE_KEY);
    if (savedColor === "dark" || savedColor === "auto") {
      colorScheme = savedColor;
    } else if (savedColor === "high-contrast") {
      colorScheme = "light";
      highContrast = true;
      localStorage.setItem(COLOR_STORAGE_KEY, "light");
      localStorage.setItem(HIGH_CONTRAST_STORAGE_KEY, "1");
    }

    if (!highContrast) {
      const savedContrast = localStorage.getItem(HIGH_CONTRAST_STORAGE_KEY);
      highContrast = savedContrast === "1" || savedContrast === "true";
    }

    let fontSizeLevel: A11yFontSizeLevel = 0;
    const savedFont = parseInt(
      localStorage.getItem(FONT_SIZE_STORAGE_KEY) ?? "0",
      10,
    );
    if (FONT_SIZE_LEVELS.includes(savedFont as A11yFontSizeLevel)) {
      fontSizeLevel = savedFont as A11yFontSizeLevel;
    }

    return { colorScheme, fontSizeLevel, highContrast };
  } catch {
    return { ...DEFAULT_A11Y_DISPLAY_PREFERENCES };
  }
}

export function persistA11yDisplayPreferences(
  prefs: A11yDisplayPreferences,
): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(COLOR_STORAGE_KEY, prefs.colorScheme);
    localStorage.setItem(
      HIGH_CONTRAST_STORAGE_KEY,
      prefs.highContrast ? "1" : "0",
    );
    localStorage.setItem(FONT_SIZE_STORAGE_KEY, String(prefs.fontSizeLevel));
  } catch {
    /* ignore */
  }
}

/** Remove user display-preference classes/attributes (e.g. when accessibility tools are disabled). */
export function clearA11yDisplayPreferencesFromDom(): void {
  applyA11yDisplayPreferences(DEFAULT_A11Y_DISPLAY_PREFERENCES, false);
}

export function applyA11yDisplayPreferences(
  prefs: A11yDisplayPreferences,
  systemPrefersDark = getSystemPrefersDark(),
): void {
  if (typeof document === "undefined") return;

  const effective = resolveEffectiveColorMode(
    prefs.colorScheme,
    systemPrefersDark,
  );
  const isDark = effective === "dark";
  const root = document.documentElement;
  const body = document.body;

  root.setAttribute("data-bs-theme", isDark ? "dark" : "light");
  root.classList.toggle("covomo-high-contrast", prefs.highContrast);
  root.classList.toggle("covomo-font-size-1", prefs.fontSizeLevel === 1);
  root.classList.toggle("covomo-font-size-2", prefs.fontSizeLevel === 2);

  if (body) {
    body.classList.toggle("theme-dark", isDark);
    body.classList.toggle("theme-light", !isDark);
    body.classList.toggle("covomo-theme-high-contrast", prefs.highContrast);
  }
}

export function getNextCycleColorScheme(
  current: A11yColorScheme,
): A11yColorScheme {
  if (current === "dark" || current === "light") return "auto";
  if (current === "auto") {
    return readAutoFrom() === "dark" ? "light" : "dark";
  }
  return "dark";
}

export function cycleColorScheme(current: A11yColorScheme): A11yColorScheme {
  const next = getNextCycleColorScheme(current);
  if (next === "auto") {
    writeAutoFrom(current === "light" ? "light" : "dark");
  }
  return next;
}

export function cycleFontSizeLevel(
  level: A11yFontSizeLevel,
): A11yFontSizeLevel {
  const index = FONT_SIZE_LEVELS.indexOf(level);
  return FONT_SIZE_LEVELS[
    (index + 1) % FONT_SIZE_LEVELS.length
  ] as A11yFontSizeLevel;
}

export type ThemeCycleUi = {
  nextMode: A11yColorScheme;
  icon: "light_mode" | "dark_mode" | "brightness_auto";
};

export function getCurrentThemeUi(colorScheme: A11yColorScheme): ThemeCycleUi {
  const icon =
    colorScheme === "dark"
      ? "dark_mode"
      : colorScheme === "auto"
        ? "brightness_auto"
        : "light_mode";
  return { nextMode: colorScheme, icon };
}

export function getThemeCycleUi(colorScheme: A11yColorScheme): ThemeCycleUi {
  const nextMode = getNextCycleColorScheme(colorScheme);
  const icon =
    nextMode === "dark"
      ? "dark_mode"
      : nextMode === "auto"
        ? "brightness_auto"
        : "light_mode";
  return { nextMode, icon };
}
